import { formatNumber } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AbstractionToken } from 'services/biconomy/accountAbstraction.types'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import SegmentedControl from 'components/SegmentedControl'
import Spacer from 'components/Spacer'
import { Body, NumericValue } from 'components/Text'
import { selectEthWalletBalance, selectV3DepositableBalances } from 'state/balances/selectors'
import { depositFundsToOneClick } from 'state/futures/actions'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import { withdrawCrossMargin } from 'state/futures/snxPerpsV3/actions'
import { selectCrossMarginMarginInfo } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import {
	BalanceContainer,
	BalanceText,
	DepositInputContainer,
	DepositInputMain,
} from '../../ManageSmartMarginModal'
import useAbstractionInfo from '../hooks/useAbstractionInfo'

import AbstractionWalletInfo from './AbstractionWalletInfo'
import FundTemplates from './FundTemplates'

enum FundTabs {
	Wallet = 'Wallet',
	Margin = 'Margin',
}

const FUND_TABS = [FundTabs.Wallet, FundTabs.Margin]

const BaseFundComponent = ({ children }: PropsWithChildren) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const txProcessing = useAppSelector(selectSubmittingFuturesTx)
	const v3Balances = useAppSelector(selectV3DepositableBalances)
	const { availableMargin } = useAppSelector(selectCrossMarginMarginInfo)

	const [value, setValue] = useState<string>('')
	const [selectedFundTabIndex, setSelectedFundTabIndex] = useState(0)

	const { usedToken, estimateTradeCost } = useAbstractionInfo()

	const walletBalance = useAppSelector(selectEthWalletBalance)

	const eoaBalance = useMemo(() => {
		return usedToken === AbstractionToken.ETH ? walletBalance : v3Balances.USDC
	}, [usedToken, walletBalance, v3Balances])

	const fundTab = useMemo(() => FUND_TABS[selectedFundTabIndex], [selectedFundTabIndex])

	const formattedBalance = useMemo(() => {
		const balance = fundTab === FundTabs.Wallet ? eoaBalance : availableMargin
		return formatNumber(balance ?? 0, { suggestDecimals: true })
	}, [eoaBalance, fundTab, availableMargin])

	const isDisabled = useMemo(() => {
		return wei(value || '0').gte(formattedBalance) || txProcessing || !value
	}, [value, formattedBalance, txProcessing])

	const handleSetMax = useCallback(() => {
		const max = (
			parseFloat(formatNumber(formattedBalance)) -
			(estimateTradeCost.asset === AbstractionToken.ETH ? parseFloat(estimateTradeCost.value) : 0)
		).toString()
		setValue(max)
	}, [formattedBalance, estimateTradeCost])

	const deposit = useCallback(async () => {
		if (fundTab === FundTabs.Margin) {
			await dispatch(
				withdrawCrossMargin({
					amount: wei(value),
					asset: 'SUSDC_V3',
					unwrapToToken: 'USDC',
				})
			)
		}

		dispatch(
			depositFundsToOneClick({
				amount: value,
				token: usedToken,
			})
		)
	}, [dispatch, fundTab, value, usedToken])

	return (
		<>
			<AbstractionWalletInfo />
			<Spacer height={30} />
			{usedToken === AbstractionToken.USDC && (
				<>
					<SegmentedControl
						values={FUND_TABS}
						selectedIndex={selectedFundTabIndex}
						onChange={setSelectedFundTabIndex}
					/>
					<Spacer height={20} />
				</>
			)}
			<BalanceContainer>
				<BalanceText>{t('futures.market.trade.margin.modal.deposit.amount')}</BalanceText>
				<BalanceText>
					{`${FUND_TABS[selectedFundTabIndex]} ${t('futures.one-click-trading.onboard.balance')}: `}
					<NumericValue size="small" as="span" onClick={handleSetMax} style={{ cursor: 'pointer' }}>
						{formattedBalance}
					</NumericValue>
				</BalanceText>
			</BalanceContainer>
			<DepositInputContainer>
				<DepositInputMain placeholder="0.00" value={value} onChange={(_, v) => setValue(v)} />
				<Body>{usedToken}</Body>
			</DepositInputContainer>
			<Spacer height={20} />
			<FundTemplates value={value} onChange={(v) => setValue(v)} valueToken={usedToken} />
			{children}
			<StyledButton loading={txProcessing} noOutline onClick={deposit} disabled={isDisabled}>
				{t('futures.one-click-trading.onboard.deposit')} {usedToken}
			</StyledButton>
		</>
	)
}

const StyledButton = styled(TransactionButton)`
	margin-top: 24px;
	height: 50px;
	width: 100%;
`

export default BaseFundComponent
